import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CaretDownIcon,
  CaretUpIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ClockCounterClockwiseIcon,
} from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import { ErrorBanner, INPUT_CLS } from "../../components/crm/Form";
import RoleBadge from "../../components/RoleBadge";
import { formatApiError } from "../../lib/api";
import {
  CATEGORIES,
  CATEGORY_LABEL,
  CATEGORY_TINT,
  DATE_RANGES,
  dateRangeToQuery,
  eventCategory,
  eventLabel,
  isoLocal,
  listAuditLog,
  relativeTime,
  targetLink,
  truncate,
} from "../../lib/auditLog";

const PAGE_SIZE = 50;

export default function AuditLogList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const category = params.get("category") || "";
  const range = params.get("range") || "30d";
  const search = params.get("search") || "";
  const page = parseInt(params.get("page") || "1", 10) || 1;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [searchInput, setSearchInput] = useState(search);
  const [expanded, setExpanded] = useState(null);

  const queryParams = useMemo(() => {
    const q = { page, page_size: PAGE_SIZE, ...dateRangeToQuery(range) };
    if (category) q.event_type = category;
    if (search) q.search = search;
    return q;
  }, [page, category, range, search]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await listAuditLog(queryParams);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    load();
  }, [load]);

  // Sync search-input → URL with 300ms debounce.
  useEffect(() => {
    setSearchInput(search);
  }, [search]);
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput === search) return;
      const next = new URLSearchParams(params);
      if (searchInput) next.set("search", searchInput);
      else next.delete("search");
      next.set("page", "1");
      setParams(next);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    next.set("page", "1");
    setParams(next);
  };

  const goToPage = (p) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title="Audit Log"
          subtitle="Complete activity history across your Zion Solar Service platform"
        />

        <ErrorBanner message={err} />

        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search descriptions or target labels…"
            data-testid="audit-search"
            className={INPUT_CLS}
          />
          <ChipRow
            label="Category"
            options={CATEGORIES}
            value={category}
            onChange={(v) => setFilter("category", v)}
            testidPrefix="audit-category"
          />
          <ChipRow
            label="Range"
            options={DATE_RANGES}
            value={range}
            onChange={(v) => setFilter("range", v || "30d")}
            testidPrefix="audit-range"
          />
          <div className="text-[11px] text-slate-500 pt-1">
            Showing {items.length} of {total}
            {totalPages > 1 ? ` · page ${page} / ${totalPages}` : ""}
          </div>
        </div>

        <div
          data-testid="audit-list"
          className="bg-white border border-slate-200 rounded-md overflow-hidden"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-4 py-2.5 w-32">Time</th>
                <th className="px-4 py-2.5">Event</th>
                <th className="px-4 py-2.5">Actor</th>
                <th className="px-4 py-2.5">Target</th>
                <th className="px-4 py-2.5">Description</th>
                <th className="px-4 py-2.5 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && <SkeletonRows count={6} cols={6} />}
              {!loading && items.length === 0 && (
                <EmptyState
                  inTable
                  colSpan={6}
                  icon={ClockCounterClockwiseIcon}
                  title="No activity found"
                  description="No audit events match the selected filters. Try widening the date range or clearing the category."
                  testId="audit-empty"
                />
              )}
              {items.map((entry) => {
                const link = targetLink(entry);
                const isOpen = expanded === entry.id;
                const cat = eventCategory(entry.event_type);
                return (
                  <ResultRows
                    key={entry.id}
                    entry={entry}
                    isOpen={isOpen}
                    onToggle={() =>
                      setExpanded(isOpen ? null : entry.id)
                    }
                    onTargetClick={() => link && navigate(link)}
                    cat={cat}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
              data-testid="audit-prev"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              <CaretLeftIcon size={12} /> Previous
            </button>
            <span className="text-xs text-slate-500">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => goToPage(page + 1)}
              data-testid="audit-next"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              Next <CaretRightIcon size={12} />
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}

function ResultRows({ entry, isOpen, onToggle, onTargetClick, cat }) {
  const tint = CATEGORY_TINT[cat] || CATEGORY_TINT.system;
  const link = targetLink(entry);
  return (
    <>
      <tr
        data-testid={`audit-row-${entry.id}`}
        className="border-t border-slate-200 hover:bg-slate-50/70"
      >
        <td className="px-4 py-2.5 align-top text-xs text-slate-700 font-data">
          <span title={isoLocal(entry.created_at)}>
            {relativeTime(entry.created_at)}
          </span>
        </td>
        <td className="px-4 py-2.5 align-top">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tint}`}
              data-testid={`audit-cat-${cat}`}
            >
              {CATEGORY_LABEL[cat] || cat}
            </span>
            <span className="text-sm font-semibold text-slate-900">
              {eventLabel(entry.event_type)}
            </span>
          </div>
        </td>
        <td className="px-4 py-2.5 align-top text-sm">
          {entry.actor_name ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-slate-900">{entry.actor_name}</span>
              {entry.actor_role && <RoleBadge role={entry.actor_role} />}
            </div>
          ) : (
            <span className="text-slate-400 italic">System</span>
          )}
        </td>
        <td className="px-4 py-2.5 align-top text-sm">
          {entry.target_label ? (
            link ? (
              <button
                type="button"
                onClick={onTargetClick}
                data-testid={`audit-target-link-${entry.id}`}
                className="text-slate-900 hover:text-amber-700 underline-offset-2 hover:underline"
              >
                {entry.target_label}
              </button>
            ) : (
              <span className="text-slate-700">{entry.target_label}</span>
            )
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="px-4 py-2.5 align-top text-sm text-slate-700">
          <span title={entry.description}>
            {truncate(entry.description, 80)}
          </span>
        </td>
        <td className="px-4 py-2.5 align-top text-right">
          <button
            type="button"
            onClick={onToggle}
            data-testid={`audit-expand-${entry.id}`}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label={isOpen ? "Collapse details" : "Expand details"}
          >
            {isOpen ? <CaretUpIcon size={14} /> : <CaretDownIcon size={14} />}
          </button>
        </td>
      </tr>
      {isOpen && (
        <tr
          className="bg-slate-50/60 border-t border-slate-200"
          data-testid={`audit-details-${entry.id}`}
        >
          <td colSpan={6} className="px-6 py-3">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
              <DetailRow label="Event type" value={entry.event_type} mono />
              <DetailRow
                label="Timestamp"
                value={isoLocal(entry.created_at)}
              />
              {entry.ip_address && (
                <DetailRow label="IP address" value={entry.ip_address} mono />
              )}
              {entry.target_type && (
                <DetailRow label="Target type" value={entry.target_type} mono />
              )}
              {entry.target_id && (
                <DetailRow label="Target id" value={entry.target_id} mono />
              )}
            </div>
            {entry.metadata && Object.keys(entry.metadata).length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
                  Metadata
                </div>
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  {Object.entries(entry.metadata).map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <dt className="font-data text-slate-500">{k}:</dt>
                      <dd className="font-data text-slate-900 break-all">
                        {typeof v === "object" ? JSON.stringify(v) : String(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="flex gap-2">
      <span className="text-slate-500">{label}:</span>
      <span
        className={`text-slate-900 break-all ${mono ? "font-data" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

function ChipRow({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1 w-16">
        {label}
      </span>
      {options.map((o) => {
        const isActive = value === o.value || (!value && o.value === "");
        return (
          <button
            key={o.value || "_all"}
            type="button"
            onClick={() => onChange(o.value)}
            data-testid={`${testidPrefix}-${o.value || "all"}`}
            className={`text-xs px-2.5 py-1 rounded-md border transition ${
              isActive
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SkeletonRows({ count = 5, cols = 6 }) {
  return Array.from({ length: count }).map((_, i) => (
    <tr key={`sk-${i}`} className="border-t border-slate-200">
      {Array.from({ length: cols }).map((__, j) => (
        <td key={j} className="px-4 py-3">
          <div className="h-3 w-full max-w-[140px] rounded bg-slate-100 animate-pulse" />
        </td>
      ))}
    </tr>
  ));
}
